import { Avatar, AvatarFallback } from "@repo/ui";
import { Badge } from "@repo/ui";
import { Button } from "@repo/ui";
import { ScrollArea } from "@repo/ui";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@repo/ui";
import { Separator } from "@repo/ui";
import {
  Clock,
  Info,
  Lock,
  Paperclip,
  Reply,
  Star,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { AVAILABLE_COLORS, COLORS_MAPPING } from "../../lib/constants";
import type { MailComponent, MailReponse, PieceJointe } from "../../lib/types";
import {
  MailStatus,
  type MailMetadata,
  type VariablesData,
} from "../../lib/types";
import type { TypeReponseMail } from "../../templates/types";
import { calculateDateWithDateRelative, getInitials, getSenderColor } from "../../lib/utils";
import { cn } from "@repo/utils";
import { useUIStore } from "../../stores/uiStore";
import { getPieceJointeContentComponent } from "../contenu-pieces-jointes";
import { AttachmentViewerModal } from "./AttachmentViewerModal";
import { MailComposeForm } from "./MailComposeForm";

/** Pause de frappe au-delà de laquelle le brouillon est sauvegardé. */
const DELAI_SAUVEGARDE_BROUILLON_MS = 1000;

interface MailDetailProps {
  CurrentMailComponent: MailComponent;
  mailResponse?: MailReponse;
  variablesData: VariablesData;
  isPresidentMail?: boolean;
  typeReponse?: TypeReponseMail;
  isCompleted?: boolean;
  onClose?: () => void;
  onColorChange?: (color: string) => void;
  onToggleImportant?: () => void;
  onSaveResponse?: (reponse: string, transfertA?: string) => void;
  onSaveDraft?: (reponse: string, transfertA?: string) => void;
  onStartDraft?: () => void;
  onCancelDraft?: () => void;
  onStartEdit?: () => void;
}

export function MailDetail({
  CurrentMailComponent,
  mailResponse,
  variablesData,
  isPresidentMail = false,
  typeReponse = "mail",
  isCompleted = false,
  onClose,
  onColorChange,
  onToggleImportant,
  onSaveResponse,
  onSaveDraft,
  onStartDraft,
  onCancelDraft,
  onStartEdit,
}: MailDetailProps) {
  const mailMetadata = CurrentMailComponent.metadata as MailMetadata;
  const todayDate = useUIStore((state) => state.todayDate);
  const [composeMode, setComposeMode] = useState<"reply" | "forward" | null>(
    null,
  );
  const [selectedAttachment, setSelectedAttachment] =
    useState<PieceJointe | null>(null);

  // État du formulaire de rédaction
  const [reponseText, setReponseText] = useState("");
  const [transferRecipients, setTransferRecipients] = useState("");

  // État de l'édition d'une réponse déjà envoyée
  const [originalResponse, setOriginalResponse] = useState<string | null>(null);
  const [originalTransferRecipients, setOriginalTransferRecipients] = useState("");

  // Ref indiquant s'il faut sauvegarder le brouillon
  const prevComposeModeRef = useRef<"reply" | "forward" | null>(null);
  // Ref pour court-circuiter l'effet de sauvegarde quand l'utilisateur envoie explicitement
  const isSendingRef = useRef(false);

  // Ref sur le dernier texte sauvegardé, pour l'auto-sauvegarde
  const refReponseText = useRef<string>("");

  // Refs toujours à jour pour les valeurs courantes (utilisés au démontage)
  const reponseTextRef = useRef(reponseText);
  reponseTextRef.current = reponseText;
  const transferRecipientsRef = useRef(transferRecipients);
  transferRecipientsRef.current = transferRecipients;
  const composeModeRef = useRef(composeMode);
  composeModeRef.current = composeMode;
  const onSaveDraftRef = useRef(onSaveDraft);
  onSaveDraftRef.current = onSaveDraft;

  // Sauvegarde le brouillon au démontage (ex : changement de mail sélectionné)
  useEffect(() => {
    return () => {
      if (
        !isSendingRef.current &&
        composeModeRef.current &&
        reponseTextRef.current.trim()
      ) {
        onSaveDraftRef.current?.(
          reponseTextRef.current,
          transferRecipientsRef.current || undefined,
        );
      }
    };
  }, []);

  // Pré-remplit les champs quand mailResponse change — mais PAS pendant que
  // l'utilisateur rédige : les réponses de sauvegarde de brouillon venant de
  // Convex écraseraient sa saisie.
  useEffect(() => {
    if (mailResponse && !composeModeRef.current) {
      setReponseText(mailResponse.reponse || "");
      setTransferRecipients(mailResponse.transfertA || "");
      // Initialise la ref pour ne pas déclencher l'auto-sauvegarde au premier rendu
      refReponseText.current = mailResponse.reponse || "";
    }
  }, [mailResponse]);

  // Auto-sauvegarde du brouillon après une PAUSE de frappe, pas à chaque
  // caractère : une sauvegarde réécrit tout le document évaluation (toutes les
  // réponses) et fait ré-exécuter les queries abonnées. Sauvegarder tous les 3
  // caractères coûtait ~1 mutation par seconde de frappe, et un volume de
  // Database I/O qui croît au carré de la longueur de la passation. Rien n'est
  // perdu : le démontage et la fermeture du mode rédaction sauvegardent le
  // texte en cours (effets ci-dessus et ci-dessous).
  useEffect(() => {
    if (!composeMode || !reponseText.trim()) return;
    if (reponseText === refReponseText.current) return;
    const minuterie = setTimeout(() => {
      onSaveDraftRef.current?.(reponseText, transferRecipients || undefined);
      refReponseText.current = reponseText;
    }, DELAI_SAUVEGARDE_BROUILLON_MS);
    return () => clearTimeout(minuterie);
  }, [reponseText, composeMode, transferRecipients]);

  // Sauvegarde à la fermeture du mode rédaction (si le texte est non vide et pas déjà envoyé)
  useEffect(() => {
    // Le mode rédaction était ouvert et vient de se fermer
    if (prevComposeModeRef.current && !composeMode) {
      // Pas de sauvegarde si l'utilisateur vient de cliquer « Envoyer »
      if (isSendingRef.current) {
        isSendingRef.current = false;
      } else if (
        reponseText.trim() &&
        mailResponse?.statut !== MailStatus.REPONDU
      ) {
        // Sauvegarde s'il y a du texte et que le statut n'est pas déjà « repondu »
        onSaveDraft?.(reponseText, transferRecipients || undefined);
      } else if (
        !reponseText.trim() &&
        mailResponse?.statut !== MailStatus.REPONDU
      ) {
        // Annule le brouillon si la zone de texte est vide
        onCancelDraft?.();
      }
    }
    prevComposeModeRef.current = composeMode;
  }, [
    composeMode,
    reponseText,
    transferRecipients,
    mailResponse?.statut,
    onSaveDraft,
    onCancelDraft,
  ]);

  const handleSend = () => {
    if (!onSaveResponse) return;

    isSendingRef.current = true;

    if (composeMode === "forward") {
      onSaveResponse(reponseText, transferRecipients || undefined);
    } else {
      onSaveResponse(reponseText);
    }

    setComposeMode(null);
  };

  const dateText = calculateDateWithDateRelative(
    mailMetadata.dateRelative,
    todayDate,
  );
  const hasAttachments =
    mailMetadata.piecesJointes && mailMetadata.piecesJointes.length > 0;
  const isUnread = !mailResponse || mailResponse.statut === MailStatus.NON_LU;
  const currentColor = mailResponse?.couleur || "bg-white";

  const handleStartEdit = () => {
    setOriginalResponse(reponseText);
    setOriginalTransferRecipients(transferRecipients);
    setComposeMode("reply");
    onStartEdit?.();
  };

  const handleCancelEdit = () => {
    isSendingRef.current = true;
    onSaveResponse?.(originalResponse!, originalTransferRecipients || undefined);
    setReponseText(originalResponse!);
    setTransferRecipients(originalTransferRecipients);
    setOriginalResponse(null);
    setOriginalTransferRecipients("");
    setComposeMode(null);
  };

  const handleClose = () => {
    onClose?.();
  };

  return (
    <div className="flex h-full flex-1 flex-col overflow-hidden bg-background">
      {/* Header with actions */}
      <header className="shrink-0 border-b bg-card px-4 py-3">
        <div className="flex items-center justify-between py-0.5">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="lg:hidden"
            >
              <X className="h-4 w-4" />
            </Button>
            <h2 className="text-lg font-semibold text-foreground truncate max-w-75">
              {mailMetadata.titre}
            </h2>
            {/* Minimal color selector */}
            <Select
              value={currentColor}
              onValueChange={(value) => onColorChange?.(value)}
              disabled={isCompleted}
            >
              <SelectTrigger
                className="h-7 w-12 border-0 p-0 shadow-none focus:ring-0"
                title="Catégorie de couleur"
              >
                <span
                  className={cn(
                    "h-5 w-5 rounded-full border-2 transition-transform hover:scale-110",
                    currentColor === "bg-white"
                      ? "border-gray-300 bg-white"
                      : cn(currentColor, "border-transparent"),
                  )}
                />
              </SelectTrigger>
              <SelectContent align="start">
                {AVAILABLE_COLORS.map((color) => (
                  <SelectItem key={color} value={color}>
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          "h-4 w-4 rounded-full border",
                          color === "bg-white"
                            ? "border-gray-300 bg-white"
                            : color,
                        )}
                      />
                      <span className="capitalize">
                        {COLORS_MAPPING[color]}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggleImportant}
              title="Marquer comme important"
            >
              <Star
                className={cn(
                  "h-4 w-4",
                  (mailResponse?.important || isPresidentMail) &&
                    "fill-yellow-400 text-yellow-400",
                )}
              />
            </Button>
          </div>
        </div>
      </header>

      {/* Completed banner */}
      {isCompleted && (
        <div className="shrink-0 flex items-center gap-2 border-b bg-muted/50 px-4 py-2 text-sm text-muted-foreground">
          <Lock className="h-3.5 w-3.5 shrink-0" />
          <span>
            Évaluation terminée — les réponses ne peuvent plus être modifiées.
          </span>
        </div>
      )}

      {/* Mail content */}
      <ScrollArea className="flex-1">
        <div className="p-6">
          {/* Sender info */}
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <Avatar className="h-12 w-12" style={{ backgroundColor: getSenderColor(mailMetadata.emetteur) }}>
                <AvatarFallback className="text-white" style={{ backgroundColor: getSenderColor(mailMetadata.emetteur) }}>
                  {getInitials(mailMetadata.emetteur)}
                </AvatarFallback>
              </Avatar>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-foreground">
                    {mailMetadata.emetteur}
                  </span>
                  {isUnread && (
                    <Badge variant="default" className="text-xs">
                      Non lu
                    </Badge>
                  )}
                </div>
                {mailMetadata.emetteurPoste && (
                  <p className="text-sm text-muted-foreground">
                    {mailMetadata.emetteurPoste}
                  </p>
                )}
                {mailMetadata.emetteurEntreprise && (
                  <p className="text-sm text-muted-foreground">
                    {mailMetadata.emetteurEntreprise}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">À : moi</p>
              </div>
            </div>

            <div className="flex flex-col items-end gap-2">
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <Clock className="h-3 w-3" />
                <span>{dateText}</span>
              </div>
              {hasAttachments && (
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Paperclip className="h-3 w-3" />
                  <span>
                    {mailMetadata.piecesJointes.length} pièce(s) jointe(s)
                  </span>
                </div>
              )}
            </div>
          </div>

          <Separator className="my-6" />

          {/* Mail body */}
          <div className="prose prose-sm max-w-none">
            {CurrentMailComponent && (
              <CurrentMailComponent variablesData={variablesData} />
            )}
          </div>

          {/* Attachments section */}
          {hasAttachments && (
            <>
              <Separator className="my-6" />
              <div>
                <p className="mb-3 text-sm font-medium text-muted-foreground">
                  Pièces jointes ({mailMetadata.piecesJointes.length})
                </p>
                <div className="space-y-2">
                  {mailMetadata.piecesJointes.map((attachmentKey, index) => {
                    const pieceJointe =
                      getPieceJointeContentComponent(attachmentKey);

                    return (
                      <button
                        key={index}
                        type="button"
                        onClick={() => {
                          if (pieceJointe) {
                            setSelectedAttachment({
                              cle: attachmentKey,
                              titre: pieceJointe.title,
                            });
                          }
                        }}
                        className="flex w-full items-center gap-2 rounded-controle border bg-muted/30 px-3 py-2 text-sm hover:bg-muted/50 transition-colors"
                      >
                        <Paperclip className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">
                          {pieceJointe?.title || attachmentKey}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* No response notice / Reply action buttons */}
          {typeReponse === "no_response" ? (
            <>
              <Separator className="my-6" />
              <div className="flex items-center gap-3 rounded-surface border border-etat-info-trait bg-etat-info-surface px-4 py-3 text-sm text-etat-info-contenu">
                <Info className="h-4 w-4 shrink-0 text-etat-info" />
                La réponse à ce mail sera intégrée à une autre question.
              </div>
            </>
          ) : null}
          {typeReponse !== "no_response" && mailResponse?.statut !== MailStatus.REPONDU && (
            <>
              <Separator className="my-6" />
              {composeMode ? (
                <MailComposeForm
                  composeMode={composeMode}
                  reponseText={reponseText}
                  transferRecipients={transferRecipients}
                  typeReponse={typeReponse}
                  emetteur={mailMetadata.emetteur}
                  originalResponse={originalResponse}
                  onReponseChange={setReponseText}
                  onTransferRecipientsChange={setTransferRecipients}
                  onSend={handleSend}
                  onClose={() =>
                    originalResponse !== null
                      ? handleCancelEdit()
                      : setComposeMode(null)
                  }
                />
              ) : (
                <div className="flex items-center gap-2">
                  {typeReponse === "mail" && (
                    <Button
                      variant="default"
                      size="sm"
                      disabled={isCompleted}
                      onClick={() => {
                        setComposeMode("reply");
                        onStartDraft?.();
                      }}
                    >
                      Répondre
                    </Button>
                  )}
                  {typeReponse === "list" && (
                    <Button
                      variant="default"
                      size="sm"
                      disabled={isCompleted}
                      onClick={() => {
                        setComposeMode("reply");
                        onStartDraft?.();
                      }}
                    >
                      Expliquer mes choix
                    </Button>
                  )}
                </div>
              )}
            </>
          )}

          {/* Saved response section */}
          {mailResponse?.reponse &&
            mailResponse.statut === MailStatus.REPONDU && (
              <>
                <Separator className="my-6" />
                {composeMode ? (
                  <MailComposeForm
                    composeMode={composeMode}
                    reponseText={reponseText}
                    transferRecipients={transferRecipients}
                    typeReponse={typeReponse}
                    emetteur={mailMetadata.emetteur}
                    originalResponse={originalResponse}
                    isEditMode={true}
                    onReponseChange={setReponseText}
                    onTransferRecipientsChange={setTransferRecipients}
                    onSend={handleSend}
                    onClose={handleCancelEdit}
                  />
                ) : (
                  <>
                    <div className="rounded-surface border-l-4 border-l-green-500 bg-green-50 p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Reply className="h-4 w-4 text-green-600" />
                        <span className="text-sm font-medium text-green-600">
                          {typeReponse === "list" ? "Vos choix" : "Votre réponse"}
                        </span>
                        {mailResponse.transfertA && (
                          <Badge variant="outline" className="text-xs">
                            Transféré à : {mailResponse.transfertA}
                          </Badge>
                        )}
                      </div>
                      <div className="whitespace-pre-wrap text-sm text-foreground">
                        {mailResponse.reponse}
                      </div>
                    </div>
                    {!isCompleted && (
                      <div className="mt-3">
                        <Button
                          variant="default"
                          size="sm"
                          onClick={handleStartEdit}
                        >
                          Modifier ma réponse
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </>
            )}

          {/* Draft indicator */}
          {mailResponse?.estEnBrouillon &&
            mailResponse?.reponse &&
            composeMode === null &&
            mailResponse.statut !== MailStatus.REPONDU && (
              <>
                <Separator className="my-6" />
                <div className="rounded-surface border-l-4 border-l-yellow-500 bg-yellow-50 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Reply className="h-4 w-4 text-yellow-600" />
                    <span className="text-sm font-medium text-yellow-600">
                      Brouillon
                    </span>
                  </div>
                  <div className="whitespace-pre-wrap text-sm text-muted-foreground">
                    {mailResponse.reponse}
                  </div>
                </div>
              </>
            )}
        </div>
      </ScrollArea>


      {/* Attachment Modal */}
      {selectedAttachment && (
        <AttachmentViewerModal
          attachment={selectedAttachment}
          variablesData={variablesData}
          onClose={() => setSelectedAttachment(null)}
        />
      )}
    </div>
  );
}
